import APIRequest from './APIRequest';
import { ImageUrl } from './API';

export const bulletinService = {
  // Get user bulletins with pagination
  getUserBulletins: async (page = 1, limit = 10) => {
    try {
      const response = await APIRequest.get(`/user/bulletins?page=${page}&limit=${limit}`);
      const res = response?.data || {};
      const pagination = res.pagination || {
        currentPage: page,
        totalPages: 1,
        totalRecords: Array.isArray(res.data) ? res.data.length : 0,
        hasNext: false,
        hasPrev: false,
      };

      // Normalize items for UI components
      const bulletins = Array.isArray(res.data)
        ? res.data.map((item) => {
            const coverPath = item.coverImage || item.imageUrl || '';
            const imageUrl = coverPath
              ? `${ImageUrl}/${coverPath}`
              : '';
            const attachments = Array.isArray(item.attachments)
              ? item.attachments.map((p) => ({
                  url: `${ImageUrl}/${p}`,
                  name: p.split('/').pop(),
                }))
              : [];
            return {
              id: item._id || item.id,
              title: item.title,
              description: item.message || item.description || '',
              category: item.category || '',
              priority: (item.priority || 'medium').toLowerCase(),
              imageUrl,
              attachments,
              createdAt: item.createdAt,
              updatedAt: item.updatedAt,
            };
          })
        : [];

      return { bulletins, pagination };
    } catch (error) {
      console.error('Error fetching user bulletins:', error);
      throw error;
    }
  },
  // Get all bulletins with pagination
  getAllBulletins: async (page = 1, limit = 10) => {
    try {
      const response = await APIRequest.get(`/admin/bulletins?page=${page}&limit=${limit}`);
      console.log('Get bulletins response:', response);
      
      // Handle different response formats
      const responseData = response.data;
      let bulletins = [];
      let pagination = {
        currentPage: page,
        totalPages: 1,
        totalRecords: 0,
        hasNext: false,
        hasPrev: false
      };
      
      if (responseData && typeof responseData === 'object') {
        // Check for different possible response structures
        if (responseData.data && Array.isArray(responseData.data)) {
          bulletins = responseData.data;
          pagination = responseData.pagination || {
            currentPage: page,
            totalPages: Math.ceil((responseData.count || bulletins.length) / limit),
            totalRecords: responseData.count || bulletins.length,
            hasNext: page < Math.ceil((responseData.count || bulletins.length) / limit),
            hasPrev: page > 1
          };
        } else if (Array.isArray(responseData)) {
          // If response is directly an array
          bulletins = responseData;
          pagination = {
            currentPage: page,
            totalPages: Math.ceil(bulletins.length / limit),
            totalRecords: bulletins.length,
            hasNext: page < Math.ceil(bulletins.length / limit),
            hasPrev: page > 1
          };
        }
      }
      
      return { bulletins, pagination };
    } catch (error) {
      console.error('Error fetching bulletins:', error);
      throw error;
    }
  },

  // Get bulletin by ID
  getBulletinById: async (id) => {
    try {
      const response = await APIRequest.get(`/admin/bulletins/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching bulletin with id ${id}:`, error);
      throw error;
    }
  },

  // Create new bulletin with FormData for admin endpoint
  createBulletin: async (bulletinData) => {
    try {
      console.log('Creating bulletin with data:', bulletinData);
      const formData = new FormData();
      
      // Add text fields
      formData.append('title', bulletinData.title);
      formData.append('category', bulletinData.category);
      formData.append('message', bulletinData.description || bulletinData.message);
      formData.append('priority', bulletinData.priority);
      
      // Add cover image if provided
      if (bulletinData.coverImage) {
        formData.append('coverImage', bulletinData.coverImage);
      }
      
      // Add attachments (max 5 files)
      if (bulletinData.attachments && bulletinData.attachments.length > 0) {
        const filesToUpload = bulletinData.attachments.slice(0, 5); // Limit to 5 files
        filesToUpload.forEach(file => {
          if (file instanceof File) {
            formData.append('attachments', file);
          }
        });
      }
      
      const response = await APIRequest.post('/admin/bulletins', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('Create bulletin response:', response);
      
      if (response.ok) {
        return response.data;
      } else {
        throw new Error(response.data?.message || 'Failed to create bulletin');
      }
    } catch (error) {
      console.error('Error creating bulletin:', error);
      throw error;
    }
  },

  // Update existing bulletin
  updateBulletin: async (id, bulletinData) => {
    try {
      const formData = new FormData();
      
      // Add text fields
      formData.append('title', bulletinData.title);
      formData.append('category', bulletinData.category);
      formData.append('message', bulletinData.description);
      formData.append('priority', bulletinData.priority);
      
      // Add cover image if provided
      if (bulletinData.coverImage) {
        formData.append('coverImage', bulletinData.coverImage);
      }
      
      // Add attachments if provided (only new files, not existing ones)
      if (bulletinData.attachments && bulletinData.attachments.length > 0) {
        bulletinData.attachments.forEach(file => {
          // Only append actual File objects, not existing attachment objects
          if (file instanceof File) {
            formData.append('attachments', file);
          }
        });
      }
      
      const response = await APIRequest.put(`/admin/bulletins/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Check if response is successful
      if (response.ok) {
        return response.data;
      } else {
        throw new Error(response.data?.message || 'Failed to update bulletin');
      }
    } catch (error) {
      console.error(`Error updating bulletin with id ${id}:`, error);
      throw error;
    }
  },

  // Delete bulletin
  deleteBulletin: async (id) => {
    try {
      console.log('Attempting to delete bulletin with ID:', id);
      const response = await APIRequest.delete(`/admin/bulletins/${id}`);
      console.log('Delete response:', response);
      
      // Check if response is successful
      if (response.ok) {
        return response.data;
      } else {
        throw new Error(response.data?.message || 'Failed to delete bulletin');
      }
    } catch (error) {
      console.error(`Error deleting bulletin with id ${id}:`, error);
      throw error;
    }
  }
};

export default bulletinService;